import React from "react";
import {
  Wand2,
  Download,
  Loader2,
  Gauge,
  Clock,
  Save,
  Trash2,
  Bookmark,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import BatchPanel from "./BatchPanel";

const Stat = ({ label, value, mono = true, testId }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">
      {label}
    </span>
    <span
      className={`${mono ? "font-mono-pro" : "font-display"} text-sm text-[#FDFDFD]`}
      data-testid={testId}
    >
      {value}
    </span>
  </div>
);

export default function Sidebar({
  file,
  roi,
  band,
  onBandChange,
  templates,
  templateName,
  onTemplateNameChange,
  onSaveTemplate,
  onApplyTemplate,
  onDeleteTemplate,
  savingTpl,
  busy,
  progress,
  result,
  onProcess,
  onDownload,
  // Batch props
  batchFiles,
  batchRunning,
  onBatchPickFiles,
  onBatchStart,
  onBatchStop,
  onBatchClear,
  onBatchRemoveItem,
  onBatchDownloadItem,
  onBatchDownloadAll,
}) {
  const sizeMB = file ? (file.size / (1024 * 1024)).toFixed(1) : null;
  const canProcess = !!file && !!roi && !busy;
  const canSaveTpl = !!roi && (templateName || "").trim().length > 0 && !savingTpl;

  return (
    <aside
      className="lg:col-span-3 bg-[#141414] border border-[#27272A] rounded-md p-6 flex flex-col gap-6 overflow-y-auto"
      data-testid="control-sidebar"
    >
      {/* --- ROI coords --- */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#A1A1AA] font-bold">
            Coordenadas ROI
          </h3>
          <span
            className={`w-2 h-2 rounded-full ${roi ? "bg-[#FFC82E]" : "bg-[#27272A]"}`}
          />
        </div>
        <div
          className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-3 grid grid-cols-2 gap-3"
          data-testid="roi-coordinates"
        >
          <Stat label="X" value={roi ? roi.x : "—"} testId="roi-x" />
          <Stat label="Y" value={roi ? roi.y : "—"} testId="roi-y" />
          <Stat label="W" value={roi ? roi.w : "—"} testId="roi-w" />
          <Stat label="H" value={roi ? roi.h : "—"} testId="roi-h" />
        </div>
        <p className="text-[10px] text-[#71717A] mt-2 leading-relaxed">
          Coordenadas absolutas en píxeles. Border-safety se aplica server-side.
        </p>
      </section>

      {/* --- Feather / Band intensity --- */}
      <section data-testid="feather-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#A1A1AA] font-bold">
            Intensidad · Feather
          </h3>
          <span
            className="font-mono-pro text-xs text-[#FFC82E]"
            data-testid="band-value"
          >
            band={band}
          </span>
        </div>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-3">
          <Slider
            value={[band]}
            min={1}
            max={20}
            step={1}
            onValueChange={(v) => onBandChange(v[0])}
            className="my-1"
            data-testid="band-slider"
          />
          <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-2 font-mono-pro">
            <span>1 · Duro</span>
            <span>10 · Suave</span>
            <span>20 · Halo</span>
          </div>
          <p className="text-[10px] text-[#71717A] mt-3 leading-relaxed">
            Aumenta para logos con bordes anti-aliased o halo translúcido.
            Valor bajo = recorte preciso; valor alto = mezcla más natural.
          </p>
        </div>
      </section>

      {/* --- Templates --- */}
      <section data-testid="templates-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#A1A1AA] font-bold inline-flex items-center gap-1.5">
            <Bookmark size={12} strokeWidth={1.75} /> Plantillas
          </h3>
          <span className="font-mono-pro text-xs text-[#71717A]">
            {templates.length}
          </span>
        </div>

        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-3 flex flex-col gap-2">
          <Input
            data-testid="template-name-input"
            value={templateName}
            onChange={(e) => onTemplateNameChange(e.target.value)}
            placeholder="ej. Canal TV-X esquina sup."
            className="h-8 text-xs bg-[#141414] border-[#27272A] text-[#FDFDFD] placeholder:text-[#52525B] focus-visible:ring-[#E01F26] focus-visible:ring-offset-0"
          />
          <button
            type="button"
            onClick={onSaveTemplate}
            disabled={!canSaveTpl}
            data-testid="save-template-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-[0.18em] py-2 rounded-sm border border-[#FFC82E] text-[#FFC82E] hover:bg-[#FFC82E] hover:text-[#0A0A0A] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#FFC82E] transition-colors"
          >
            {savingTpl ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Save size={12} strokeWidth={2} />
            )}
            Guardar ROI actual
          </button>

          <div
            className="flex flex-col gap-1.5 mt-1 max-h-44 overflow-y-auto"
            data-testid="template-list"
          >
            {templates.length === 0 && (
              <div className="text-[11px] text-[#52525B] italic px-1 py-2">
                Sin plantillas. Crea una con un ROI y nombre.
              </div>
            )}
            {templates.map((t) => (
              <div
                key={t._id}
                className="group flex items-center justify-between gap-2 bg-[#141414] border border-[#27272A] rounded-sm px-2 py-1.5 hover:border-[#FFC82E]/40 transition-colors"
                data-testid={`template-item-${t._id}`}
              >
                <button
                  type="button"
                  onClick={() => onApplyTemplate(t)}
                  data-testid={`apply-template-${t._id}`}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="text-xs font-display font-semibold text-[#FDFDFD] truncate">
                    {t.name}
                  </div>
                  <div className="text-[10px] font-mono-pro text-[#71717A] truncate">
                    {t.roi.x},{t.roi.y} · {t.roi.w}×{t.roi.h} · b{t.band}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => onDeleteTemplate(t)}
                  data-testid={`delete-template-${t._id}`}
                  className="p-1 text-[#71717A] hover:text-[#E01F26] transition-colors"
                  title="Eliminar plantilla"
                >
                  <Trash2 size={12} strokeWidth={1.75} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- File info --- */}
      <section>
        <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#A1A1AA] font-bold mb-3">
          Archivo
        </h3>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-3">
          {file ? (
            <>
              <div className="text-sm truncate" title={file.name}>
                {file.name}
              </div>
              <div className="text-xs text-[#71717A] mt-1 font-mono-pro">
                {sizeMB} MB · {file.type || "video"}
              </div>
            </>
          ) : (
            <div className="text-xs text-[#71717A]">Sin archivo cargado</div>
          )}
        </div>
      </section>

      {/* --- Batch processing --- */}
      <BatchPanel
        files={batchFiles}
        running={batchRunning}
        canStart={!!roi && batchFiles.length > 0}
        onPickFiles={onBatchPickFiles}
        onStart={onBatchStart}
        onStop={onBatchStop}
        onClear={onBatchClear}
        onRemoveItem={onBatchRemoveItem}
        onDownloadItem={onBatchDownloadItem}
        onDownloadAll={onBatchDownloadAll}
      />

      {/* --- Process / Download --- */}
      <div className="mt-auto flex flex-col gap-3">
        {busy && (
          <div className="flex flex-col gap-2" data-testid="processing-state">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em]">
              <span className="text-[#FFC82E] inline-flex items-center gap-1.5">
                <Loader2 className="animate-spin" size={12} strokeWidth={2} />
                Procesando
              </span>
              <span className="font-mono-pro text-[#A1A1AA]">{progress}%</span>
            </div>
            {progress >= 100 ? (
              <div className="shimmer-track h-2 rounded-full" />
            ) : (
              <Progress
                value={progress}
                className="h-2 bg-[#27272A]"
                data-testid="processing-progress"
              />
            )}
            <p className="text-[10px] text-[#71717A] leading-relaxed">
              {progress < 100
                ? "Subiendo archivo al motor FFmpeg…"
                : "Aplicando filtro delogo en servidor."}
            </p>
          </div>
        )}

        {result && (
          <div
            className="border border-[#FFC82E]/40 bg-[#FFC82E]/5 p-3 rounded-sm"
            data-testid="result-panel"
          >
            <div className="flex items-center gap-2 text-[#FFC82E] text-[11px] uppercase tracking-[0.22em] font-bold mb-2">
              <Gauge size={13} strokeWidth={2} /> Listo
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Stat
                label="Tiempo"
                value={`${result.elapsed_sec}s`}
                testId="result-elapsed"
              />
              <Stat
                label="Band"
                value={result.roi_applied.band ?? band}
                testId="result-band"
              />
            </div>
            <button
              type="button"
              onClick={onDownload}
              data-testid="download-btn"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#FFC82E] hover:bg-[#D9AA26] text-[#0A0A0A] font-bold py-2.5 rounded-sm text-sm transition-colors"
            >
              <Download size={15} strokeWidth={2} /> Descargar MP4
            </button>
            <p className="text-[10px] text-[#71717A] mt-2 inline-flex items-center gap-1">
              <Clock size={10} /> Se borrará del servidor tras descargar.
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={onProcess}
          disabled={!canProcess}
          data-testid="process-video-btn"
          className="w-full btn-primary py-4 rounded-sm flex justify-center items-center gap-2 text-sm uppercase tracking-[0.18em]"
        >
          {busy ? (
            <>
              <Loader2 className="animate-spin" size={16} /> Procesando
            </>
          ) : (
            <>
              <Wand2 size={16} strokeWidth={2} />
              Procesar Video
            </>
          )}
        </button>
        {!file && (
          <p className="text-[10px] text-[#71717A] text-center">
            Carga un archivo para comenzar.
          </p>
        )}
        {file && !roi && (
          <p
            className="text-[10px] text-[#A1A1AA] text-center"
            data-testid="missing-roi-hint"
          >
            Dibuja un rectángulo sobre el logo (o aplica una plantilla).
          </p>
        )}
      </div>
    </aside>
  );
}
